<template>
    <base-layout>
        <h1 slot="header">
            {{ `app.pages.statistics.index` | trans }}
        </h1>
        <v-app id="vuetify-app">
            <v-tabs icons-and-text v-model="selectedTab" slider-color="success">
                <v-tab href="#orders">
                    {{ `app.statistics.orders.orders`|trans }}
                    <v-icon>text_snippet</v-icon>
                </v-tab>
                <v-tab href="#subscriptions">
                    {{ `app.statistics.subscriptions.subscriptions` | trans }}
                    <v-icon>credit_card</v-icon>
                </v-tab>
                <v-tab href="#users">
                    {{ `app.statistics.users.users` | trans }}
                    <v-icon>person</v-icon>
                </v-tab>
                <v-tab-item
                        :key="`orders`"
                        :value="`orders`"
                >
                    <v-container fluid>
                        <h5>{{ `app.statistics.orders.sub_header`|trans }}</h5>
                        <statistics-table ref="ordersTable" @fetched="ordersFetched" :headers="ordersHeaders"
                                          :url="routes.data"
                                          :type="'orders'">
                            <template #content="{item}">
                                <td>
                                    {{ item.date }}
                                </td>
                                <td>
                                    {{ item.paid_orders }}
                                </td>
                                <td>
                                    {{ item.aggregated_amount }}
                                </td>
                                <td>
                                    {{ item.premium_30_days }}
                                </td>
                                <td>
                                    {{ item.premium_180_days }}
                                </td>
                                <td>
                                    {{ item.premium_365_days }}
                                </td>
                                <td>
                                    {{ item.premium_total }}
                                </td>
                                <td>
                                    {{ item.standard_30_days }}
                                </td>
                                <td>
                                    {{ item.standard_180_days }}
                                </td>
                                <td>
                                    {{ item.standard_365_days }}
                                </td>
                                <td>
                                    {{ item.standard_total }}
                                </td>
                                <td>
                                    {{ item.student_30_days }}
                                </td>
                                <td>
                                    {{ item.student_180_days }}
                                </td>
                                <td>
                                    {{ item.student_365_days }}
                                </td>
                                <td>
                                    {{ item.student_total }}
                                </td>
                                <td>
                                    {{ item.individual_orders }}
                                </td>
                            </template>
                        </statistics-table>
                        <orders-all-chart ref="ordersAllChart"></orders-all-chart>
                        <orders-chart ref="ordersChart"></orders-chart>
                        <orders-amount-chart ref="ordersAmountChart"></orders-amount-chart>
                    </v-container>
                </v-tab-item>
                <v-tab-item
                        :key="`subscriptions`"
                        :value="`subscriptions`"
                >
                    <v-container fluid>
                        <h5>{{ `app.statistics.subscriptions.sub_header`|trans }}</h5>
                        <statistics-table ref="subscriptionsTable" @fetched="subscriptionsFetched"
                                          :headers="subscriptionsHeaders"
                                          :url="routes.data"
                                          :type="'subscriptions'">
                            <template #content="{item}">
                                <td>
                                    {{ item.date }}
                                </td>
                                <td>
                                    {{ item.premium_30_days }}
                                </td>
                                <td>
                                    {{ item.premium_180_days }}
                                </td>
                                <td>
                                    {{ item.premium_365_days }}
                                </td>
                                <td>
                                    {{ item.premium_total }}
                                </td>
                                <td>
                                    {{ item.standard_30_days }}
                                </td>
                                <td>
                                    {{ item.standard_180_days }}
                                </td>
                                <td>
                                    {{ item.standard_365_days }}
                                </td>
                                <td>
                                    {{ item.standard_total }}
                                </td>
                                <td>
                                    {{ item.student_30_days }}
                                </td>
                                <td>
                                    {{ item.student_180_days }}
                                </td>
                                <td>
                                    {{ item.student_365_days }}
                                </td>
                                <td>
                                    {{ item.student_total }}
                                </td>
                            </template>
                        </statistics-table>
                        <subscriptions-chart ref="subscriptionsChart"></subscriptions-chart>
                        <v-divider></v-divider>
                        <h5>{{ `app.statistics.subscriptions.sub_header_users_list`|trans }}</h5>
                        <statistics-table ref="usersListTable" :headers="usersListHeaders" :url="routes.data"
                                          :default-sort-by="`user_id`"
                                          :type="'users_list'"
                                          :enable-subscription-type="true"
                                          @usersListDownloaded="usersListDownloaded()"
                                          @usersListDownloadError="usersListDownloadedError()"
                        >
                            <template #header_actions>
                                <loading-button :loading="usersListDownloadLoading" :status="usersListDownloadStatus"
                                                @click="downloadUsersList()"
                                                style="height:48px;">
                                    {{ 'app.download' | trans }}
                                </loading-button>
                            </template>
                            <template #content="{item}">
                                <td>
                                    {{ item.id }}
                                </td>
                                <td>
                                    {{ item.name }}
                                </td>
                                <td>
                                    {{ item.email }}
                                </td>
                                <td>
                                    {{ trans(`app.statistics.${item.subscription_type}`) }}
                                </td>
                            </template>
                        </statistics-table>
                    </v-container>
                </v-tab-item>
                <v-tab-item
                        :key="`users`"
                        :value="`users`"
                >
                    <v-container fluid>
                        <h5>{{ `app.statistics.users.sub_header`|trans }}</h5>
                        <statistics-table ref="usersTable" @fetched="usersFetched" :headers="usersHeaders"
                                          :url="routes.data"
                                          :type="'users'">
                            <template #content="{item}">
                                <td>
                                    {{ item.date }}
                                </td>
                                <td>
                                    {{ item.total_users }}
                                </td>
                                <td>
                                    {{ item.registrations }}
                                </td>
                                <td>
                                    {{ item.inactive_users }}
                                </td>
                                <td>
                                    {{ item.active_education_users }}
                                </td>
                                <td>
                                    {{ item.test_users }}
                                </td>
                                <td>
                                    {{ item.premium_30_days }}
                                </td>
                                <td>
                                    {{ item.premium_180_days }}
                                </td>
                                <td>
                                    {{ item.premium_365_days }}
                                </td>
                                <td>
                                    {{ item.premium_total }}
                                </td>
                                <td>
                                    {{ item.standard_30_days }}
                                </td>
                                <td>
                                    {{ item.standard_180_days }}
                                </td>
                                <td>
                                    {{ item.standard_365_days }}
                                </td>
                                <td>
                                    {{ item.standard_total }}
                                </td>
                                <td>
                                    {{ item.student_30_days }}
                                </td>
                                <td>
                                    {{ item.student_180_days }}
                                </td>
                                <td>
                                    {{ item.student_365_days }}
                                </td>
                                <td>
                                    {{ item.student_total }}
                                </td>
                            </template>
                        </statistics-table>
                        <users-chart ref="usersChart"></users-chart>
                    </v-container>
                </v-tab-item>
            </v-tabs>
        </v-app>
    </base-layout>
</template>
<script>
import {trans} from "../../../app";
import OrdersChart from "./OrdersChart.vue";
import OrdersAllChart from "./OrdersAllChart.vue";
import OrdersAmountChart from "./OrdersAmountChart.vue";
import SubscriptionsChart from "./SubscriptionsChart.vue";
import UsersChart from "./UsersChart.vue";

export default {
    components: {SubscriptionsChart, OrdersChart, OrdersAllChart, OrdersAmountChart, UsersChart},
    props: {
        routes: Object
    },
    data: () => ({
        usersListDownloadLoading: false,
        usersListDownloadStatus: '',
        commonHeaders: [
            {
                text: trans('app.statistics.premium_30_days'),
                align: 'left',
                value: 'premium_30_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.premium_180_days'),
                align: 'left',
                value: 'premium_180_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.premium_365_days'),
                align: 'left',
                value: 'premium_365_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.premium_total'),
                align: 'left',
                value: 'premium_total',
                sortable: true,
            },
            {
                text: trans('app.statistics.standard_30_days'),
                align: 'left',
                value: 'standard30_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.standard_180_days'),
                align: 'left',
                value: 'standard_180_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.standard_365_days'),
                align: 'left',
                value: 'standard_365_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.standard_total'),
                align: 'left',
                value: 'standard_total',
                sortable: true,
            },
            {
                text: trans('app.statistics.student_30_days'),
                align: 'left',
                value: 'student_30_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.student_180_days'),
                align: 'left',
                value: 'student_180_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.student_365_days'),
                align: 'left',
                value: 'student_365_days',
                sortable: true,
            },
            {
                text: trans('app.statistics.student_total'),
                align: 'left',
                value: 'student_total',
                sortable: true,
            },
        ],
        selectedTab: null,
    }),
    created() {
        this.selectedTab = 'orders';
    },
    computed: {
        ordersHeaders() {
            return [
                {
                    text: trans('app.date'),
                    align: 'left',
                    value: 'date',
                    sortable: true,
                },
                {
                    text: trans('app.statistics.orders.paid_orders'),
                    align: 'left',
                    value: 'paid_orders',
                    sortable: true,
                },
                {
                    text: trans('app.statistics.orders.aggregated_amount'),
                    align: 'left',
                    value: 'aggregated_amount',
                    sortable: true,
                },
                ...this.commonHeaders,
                {
                    text: trans('app.statistics.orders.individual_orders'),
                    align: 'left',
                    value: 'individual_orders',
                    sortable: true,
                },
            ]
        },
        usersHeaders() {
            return [
                {
                    text: trans('app.date'),
                    align: 'left',
                    value: 'date',
                    sortable: true,
                },
                {
                    text: trans('app.statistics.users.total_users'),
                    align: 'left',
                    value: 'total_users',
                    sortable: true,
                },
                {
                    text: trans('app.statistics.users.registrations'),
                    align: 'left',
                    value: 'registrations',
                    sortable: true,
                },
                {
                    text: trans('app.statistics.users.inactive_users'),
                    align: 'left',
                    value: 'inactive_users',
                    sortable: true,
                },
                {
                    text: trans('app.statistics.users.active_education_users'),
                    align: 'left',
                    value: 'active_education_users',
                    sortable: true,
                },
                {
                    text: trans('app.statistics.users.test_users'),
                    align: 'left',
                    value: 'test_users',
                    sortable: true,
                },
                ...this.commonHeaders,
            ]
        },
        subscriptionsHeaders() {
            return [
                {
                    text: trans('app.date'),
                    align: 'left',
                    value: 'date',
                    sortable: true,
                },
                ...this.commonHeaders,
            ]
        },
        usersListHeaders() {
            return [
                {
                    text: trans('app.id'), align: 'left', value: 'id', sortable: true,
                },
                {
                    text: trans('app.name'), align: 'left', value: 'name', sortable: true,
                },
                {
                    text: trans('app.email'), align: 'left', value: 'email', sortable: true,
                },
                {
                    text: trans('app.pages.subscription_types'), align: 'left', value: 'subscription_type', sortable: true,
                }
            ]
        },
    },
    methods: {
        ordersFetched(items) {
            this.$refs.ordersAllChart.setOrdersData(items);
            this.$refs.ordersChart.setOrdersData(items);
            this.$refs.ordersAmountChart.setOrdersData(items);
        },
        subscriptionsFetched(items) {
            this.$refs.subscriptionsChart.setSubscriptionsData(items);
        },
        usersFetched(items) {
            this.$refs.usersChart.setUsersData(items);
        },
        downloadUsersList() {
            this.usersListDownloadLoading = true;
            this.$refs.usersListTable.downloadUsersList();
        },
        usersListDownloaded() {
            this.usersListDownloadLoading = false;
            this.usersListDownloadStatus = true;
        },
        usersListDownloadedError() {
            this.usersListDownloadLoading = false;
            this.usersListDownloadStatus = false;
        }
    },
    watch: {
        selectedTab: {
            handler() {
                switch (this.selectedTab) {
                    case 'orders':
                        this.$refs.ordersTable.fetchData();
                        break;
                    case 'subscriptions':
                        this.$refs.subscriptionsTable.fetchData();
                        this.$refs.usersListTable.fetchData();
                        break;
                    case 'users':
                        this.$refs.usersTable.fetchData();
                        break;
                }
            }
        }
    },
}
</script>